import { useEffect, useRef } from "react";
import gsap from "gsap";

// Animated wide wordmark: fades in, holds tucked, then the dimension line
// grows rightward and pushes AGE into place with an elastic snap while a
// frontage figure counts up and fades. Plays once on mount (every page load).
const WideAnimated = ({ className }) => {
  const rootRef = useRef(null);
  const segRef = useRef(null);
  const barRef = useRef(null);
  const capRRef = useRef(null);
  const readRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const seg = segRef.current;
    const bar = barRef.current;
    const capR = capRRef.current;
    const read = readRef.current;
    if (!root || !seg) return;

    const fs = parseFloat(getComputedStyle(root).fontSize) || 20;
    const W = 2.15 * fs;
    const cap = 0.11 * fs;
    const tuck = cap * 1.5;

    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      gsap.set(root, { opacity: 1 });
      seg.style.width = W + "px";
      bar.style.width = (W - cap) + "px";
      capR.style.left = (W - cap) + "px";
      return;
    }

    seg.style.width = tuck + "px";
    bar.style.width = "0px";
    capR.style.left = "0px";

    const pos = { w: tuck };
    const n = { v: 0 };
    const tl = gsap.timeline({ paused: true });
    tl.fromTo(root, { opacity: 0, y: 5 }, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, 0);
    const g = 0.5 + 0.45;
    tl.to(pos, {
      w: W, duration: 1.55, ease: "elastic.out(1,0.5)",
      onUpdate: () => {
        const w = Math.max(tuck, pos.w);
        seg.style.width = w + "px";
        bar.style.width = (w - cap) + "px";
        capR.style.left = (w - cap) + "px";
      },
    }, g);
    tl.to(read, { opacity: 1, duration: 0.18 }, g + 0.05);
    tl.to(n, { v: 18.6, duration: 0.7, ease: "power2.out", onUpdate: () => { read.textContent = n.v.toFixed(1) + "m"; } }, g);
    tl.to(read, { opacity: 0, y: -5, duration: 0.4, ease: "power1.in" }, g + 1.4);
    tl.timeScale(1.5); // 50% faster

    // gate the start with a real wall-clock delay (timeScale scales a timeline's own
    // delay, which was pulling the start earlier); fires when the hero's "Start a
    // campaign" button begins. the animation itself still plays 1.5x.
    const start = gsap.delayedCall(1.3, () => tl.play());
    return () => { start.kill(); tl.kill(); };
  }, []);

  return (
    <span
      ref={rootRef}
      className={`inline-flex items-center gap-[0.3em] font-extrabold tracking-[-0.01em] leading-none ${className}`}
      style={{ opacity: 0 }}
      aria-label="Frontage"
    >
      <span>FRONT</span>
      <span ref={segRef} className="relative inline-block" style={{ height: "1em" }} aria-hidden="true">
        <span
          ref={readRef}
          className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap"
          style={{ top: "-1.15em", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 400, fontSize: "0.34em", letterSpacing: "0.03em", color: "#ff4f00", opacity: 0 }}
        >
          0.0m
        </span>
        <span ref={barRef} className="absolute left-0 top-1/2 -translate-y-1/2 bg-[#ff4f00]" style={{ height: "0.11em", width: 0 }} />
        <span className="absolute left-0 top-1/2 -translate-y-1/2 bg-[#ff4f00]" style={{ width: "0.11em", height: "0.6em" }} />
        <span ref={capRRef} className="absolute top-1/2 -translate-y-1/2 bg-[#ff4f00]" style={{ left: 0, width: "0.11em", height: "0.6em" }} />
      </span>
      <span>AGE</span>
    </span>
  );
};

export const Logo = ({ variant = "wide", mono = false, animate = false, className = "" }) => {
  const lineColor = mono ? "" : "text-[#ff4f00]";

  if (variant === "monogram") {
    return (
      <svg viewBox="0 0 64 64" className={className} aria-label="Frontage monogram" role="img">
        <g fill="currentColor">
          <rect x="25" y="8" width="7" height="34" />
          <rect x="25" y="8" width="18" height="7" />
          <rect x="25" y="23" width="14" height="6" />
        </g>
        <g fill={mono ? "currentColor" : "#ff4f00"}>
          <rect x="14" y="46" width="2" height="12" />
          <rect x="14" y="51" width="36" height="2" />
          <rect x="48" y="46" width="2" height="12" />
        </g>
      </svg>
    );
  }

  if (variant === "compact") {
    return (
      <span className={`inline-flex items-center gap-[0.45em] font-extrabold tracking-[-0.01em] ${className}`}>
        <span className="flex flex-col leading-[0.95] text-left">
          <span>FRONT</span>
          <span>AGE</span>
        </span>
        <svg style={{ width: "0.55em", height: "1.9em" }} viewBox="0 0 12 44" fill="none" className={lineColor} aria-hidden="true">
          <line x1="1" y1="1" x2="11" y2="1" stroke="currentColor" strokeWidth="1.5" />
          <line x1="6" y1="1" x2="6" y2="43" stroke="currentColor" strokeWidth="1.5" />
          <line x1="1" y1="43" x2="11" y2="43" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </span>
    );
  }

  if (animate) return <WideAnimated className={className} />;

  return (
    <span className={`inline-flex items-center gap-[0.35em] font-extrabold tracking-[-0.01em] leading-none ${className}`} aria-label="Frontage">
      <span>FRONT</span>
      <svg style={{ width: "2.15em", height: "0.8em" }} viewBox="0 0 36 12" fill="none" className={lineColor} aria-hidden="true">
        <line x1="1" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.5" />
        <line x1="1" y1="6" x2="35" y2="6" stroke="currentColor" strokeWidth="1.5" />
        <line x1="35" y1="1" x2="35" y2="11" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      <span>AGE</span>
    </span>
  );
};
